import {
  AccessMode,
  Effort,
  McpApproval,
  ProfileAccess,
  type ProfileScope,
  RunMcpMode,
  RunnerLeaseView,
  type RunnerLogins,
  RunnerLogins as RunnerLoginsSchema,
  RunnerServes,
} from "@minga/protocol"
import { queryOptions } from "@tanstack/react-query"
import { Option, Schema } from "effect"
import { ApiError, apiJson, apiRequest, Instant } from "@/lib/api"
import type { ConfigPatch } from "./config"
import type { UpdateStatusInfo } from "./format"
import { parseManifest } from "./manifest"

/**
 * Runner pairing + connection contract (HTTP side), as specified for the web track:
 *   GET    /api/runners/pair/:userCode                 → PairRequest
 *   POST   /api/runners/pair/:userCode/approve {workspaceId} → { runnerId }
 *   POST   /api/runners/pair/:userCode/deny            → 204
 *   GET    /api/workspaces/:workspaceId/runners        → RunnerRow[]
 *   DELETE /api/workspaces/:workspaceId/runners/:id    → revoke (owner only)
 * Runner console v2:
 *   PATCH  /api/workspaces/:ws/runners/:id/config {capacity?, autoUpdate?, profiles?} (owner)
 *   POST   /api/workspaces/:ws/runners/:id/refresh  (owner) → runner re-probes, sends a manifest
 *   POST   /api/workspaces/:ws/runners/:id/update   (owner) → runner self-updates when idle
 *   GET    /api/runner/latest (public)              → LatestRelease
 */

export const PairRequest = Schema.Struct({
  name: Schema.String,
  os: Schema.String,
  arch: Schema.String,
  version: Schema.String,
  requestedAt: Instant,
  expiresAt: Instant,
})
export type PairRequest = typeof PairRequest.Type

const Approved = Schema.Struct({ runnerId: Schema.String })

/** The stored CLI wish of a profile (`ProfileCliWish`); the override values are kept as plain strings. */
const CliWishSchema = Schema.Struct({
  accessMode: Schema.optionalKey(AccessMode),
  claudePermissionMode: Schema.optionalKey(Schema.NullOr(Schema.String)),
  claudeSandbox: Schema.optionalKey(Schema.NullOr(Schema.Boolean)),
  codexSandbox: Schema.optionalKey(Schema.NullOr(Schema.String)),
  codexApprovalPolicy: Schema.optionalKey(Schema.NullOr(Schema.String)),
  mcp: Schema.optionalKey(RunMcpMode),
  mcpApproval: Schema.optionalKey(McpApproval),
  autoNetwork: Schema.optionalKey(Schema.Boolean),
  modelOverride: Schema.optionalKey(Schema.NullOr(Schema.String)),
  effortOverride: Schema.optionalKey(Schema.NullOr(Effort)),
})

const ProfileConfig = Schema.Struct({
  id: Schema.String,
  enabled: Schema.optionalKey(Schema.Boolean),
  capacity: Schema.optionalKey(Schema.Number),
  cli: Schema.optionalKey(CliWishSchema),
})

/** The owner's wishes, stored by the hub and applied by the runner (live, or on its next connect). */
export const DesiredConfig = Schema.Struct({
  capacity: Schema.optionalKey(Schema.Number),
  autoUpdate: Schema.optionalKey(Schema.Boolean),
  profiles: Schema.optionalKey(Schema.Array(ProfileConfig)),
  /** pending: not yet acknowledged by the runner; applied; rejected: the runner refused it (`error`). */
  status: Schema.optionalKey(Schema.Literals(["pending", "applied", "rejected"])),
  error: Schema.optionalKey(Schema.NullOr(Schema.String)),
  updatedAt: Schema.optionalKey(Schema.NullOr(Instant)),
})
export type DesiredConfig = typeof DesiredConfig.Type

export const RunnerRow = Schema.Struct({
  runnerId: Schema.String,
  name: Schema.String,
  os: Schema.String,
  arch: Schema.String,
  version: Schema.String,
  ownerUserId: Schema.String,
  status: Schema.Literals(["online", "offline"]),
  lastSeenAt: Schema.optionalKey(Schema.NullOr(Instant)),
  // These three are decoded separately (parseManifest, parseDesiredConfig, parsePendingUpdate) so
  // one malformed field never hides the runner list.
  manifest: Schema.optionalKey(Schema.Unknown),
  desiredConfig: Schema.optionalKey(Schema.Unknown),
  pendingUpdate: Schema.optionalKey(Schema.Unknown),
  /** PROVISIONAL (hub doesn't relay it yet): the runner's last `update.status`, see parseUpdateStatus. */
  updateStatus: Schema.optionalKey(Schema.Unknown),
  /** Latest published runner version, and whether it's newer and has a build for this runner's platform. */
  latestVersion: Schema.optionalKey(Schema.NullOr(Schema.String)),
  updateAvailable: Schema.optionalKey(Schema.Boolean),
  /** H8 `leases` (spec §4.4): the runs this runner holds now. Decoded one by one with `parseLeases`. */
  leases: Schema.optionalKey(Schema.Unknown),
  /** H8 `features` (settings wave): what the runner understands, e.g. "cli_settings_v2" from 0.3.0. */
  features: Schema.optionalKey(Schema.Array(Schema.String)),
  /** Wave E (decision 0063): Just me (`owner`) or The team (`team`). Absent = `owner` (a server from before E). */
  serves: Schema.optionalKey(RunnerServes),
  /** Decision 0128: Personal / Team per profile (and, on the Platform Runner, whose subscription each is). */
  profileAccess: Schema.optionalKey(Schema.Record(Schema.String, ProfileAccess)),
  /** Wave E: the machine's logins line (decoded on its own with `parseLogins`); absent on a redacted row. */
  logins: Schema.optionalKey(Schema.Unknown),
  /**
   * Wave E: someone else's personal machine. Only the public keys carry values (name, OS, owner, state, Just me / The
   * team); manifest, config and leases are null or absent, so the card shows the short "Just Becky's" form.
   */
  redacted: Schema.optionalKey(Schema.Boolean),
  /**
   * Decision 0168: on a redacted row, the logins there this viewer may use (`{ id, agent, ready }`), decoded one by one
   * where they are read (`toRunnerV1`), so an odd entry never hides the list.
   */
  sharedProfiles: Schema.optionalKey(Schema.Unknown),
  /**
   * Wave E, owner only: the last re-key (`{ state: pending | done | failed | repair, at, error? }`) and when a shared
   * machine last reconnected from a new address. Decoded on their own (`rekeyLine`), so a bad value never hides the list.
   */
  rekey: Schema.optionalKey(Schema.Unknown),
  newAddressAt: Schema.optionalKey(Schema.Unknown),
  /** Runs leased to it now (a count; the live push carries it for every member). */
  activeRuns: Schema.optionalKey(Schema.Number),
  /**
   * Decision 0149: the machine's load now, its peaks and its state (`RunnerLoadView`), decoded on its own with
   * `parseLoad` so an odd value never hides the list. Absent: an older runner or server, or no recent heartbeat.
   */
  load: Schema.optionalKey(Schema.Unknown),
  /** v1 fields kept for `toRunnersV1` (the project pages read them): the repos it has and its protocol. */
  repos: Schema.optionalKey(Schema.Unknown),
  protocol: Schema.optionalKey(Schema.Unknown),
})
export type RunnerRow = typeof RunnerRow.Type

const decodeLogins = Schema.decodeUnknownOption(RunnerLoginsSchema)
/** Wave E: the logins line of a row, or null (redacted, an older server, or a shape this page can't read). */
export const parseLogins = (value: unknown): RunnerLogins | null => Option.getOrNull(decodeLogins(value))

const decodeLease = Schema.decodeUnknownOption(RunnerLeaseView)
/** H8 `leases`, item by item: a lease this page can't read is dropped on its own. */
export const parseLeases = (value: unknown): ReadonlyArray<RunnerLeaseView> =>
  Array.isArray(value) ? value.flatMap((item) => Option.toArray(decodeLease(item))) : []

const RunnerList = Schema.Array(RunnerRow)

export { parseManifest }

const decodeDesired = Schema.decodeUnknownOption(DesiredConfig)
export const parseDesiredConfig = (value: unknown): DesiredConfig | null =>
  Option.getOrNull(decodeDesired(value))

const PendingUpdate = Schema.Union([
  Schema.String,
  Schema.Struct({ version: Schema.String, requestedAt: Schema.optionalKey(Schema.NullOr(Instant)) }),
])
const decodePending = Schema.decodeUnknownOption(PendingUpdate)
/** `pendingUpdate` as `{ version, requestedAt }` (the hub may send just the version string). */
export const parsePendingUpdate = (
  value: unknown,
): { readonly version: string; readonly requestedAt: number | string | null } | null => {
  const decoded = Option.getOrNull(decodePending(value))
  if (decoded === null) return null
  return typeof decoded === "string"
    ? { version: decoded, requestedAt: null }
    : { version: decoded.version, requestedAt: decoded.requestedAt ?? null }
}

const UpdateStatus = Schema.Struct({
  state: Schema.Literals(["waiting_idle", "downloading", "installed", "up_to_date", "failed", "unsupported"]),
  version: Schema.optionalKey(Schema.NullOr(Schema.String)),
  message: Schema.optionalKey(Schema.NullOr(Schema.String)),
  at: Schema.optionalKey(Schema.NullOr(Instant)),
})
const decodeUpdateStatus = Schema.decodeUnknownOption(UpdateStatus)
/** The runner's last self-update report (`{ state, version?, message?, at? }`), or null. */
export const parseUpdateStatus = (value: unknown): UpdateStatusInfo | null => {
  const s = Option.getOrNull(decodeUpdateStatus(value))
  if (!s) return null
  const at = s.at === undefined || s.at === null ? null : typeof s.at === "number" ? s.at : Date.parse(s.at)
  return {
    state: s.state,
    version: s.version ?? null,
    message: s.message ?? null,
    at: at !== null && Number.isFinite(at) ? at : null,
  }
}

// ---------------------------------------------------------------------------------------------
// Workspace roles (who may revoke: the runner's owner or a workspace owner; see workspaceRoleQuery)
// ---------------------------------------------------------------------------------------------

/** Same check as the server's `hasRole`: roles may be a comma-separated list. */
export const hasRole = (roles: string | null | undefined, role: string): boolean =>
  (roles ?? "")
    .split(",")
    .map((r) => r.trim())
    .includes(role)

// ---------------------------------------------------------------------------------------------
// Releases (public): GET /api/runner/latest
// ---------------------------------------------------------------------------------------------

export const ReleaseAsset = Schema.Struct({ url: Schema.String, sha256: Schema.String, size: Schema.Number })
export const LatestRelease = Schema.Struct({
  version: Schema.String,
  publishedAt: Schema.optionalKey(Schema.NullOr(Schema.String)),
  assets: Schema.Record(Schema.String, ReleaseAsset),
})
export type LatestRelease = typeof LatestRelease.Type

/** The latest published runner, or null while nothing is published (404). */
export const latestReleaseQuery = queryOptions({
  queryKey: ["runner", "latest"],
  queryFn: async (): Promise<LatestRelease | null> => {
    try {
      return await apiJson("/api/runner/latest", LatestRelease)
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  },
  staleTime: 5 * 60_000,
  retry: false,
})

/** "abcd efgh", "ABCDEFGH" → "ABCD-EFGH". Returns null until it has eight code characters. */
export const normalizeUserCode = (input: string): string | null => {
  const chars = input.toUpperCase().replace(/[^A-Z0-9]/g, "")
  if (chars.length !== 8) return null
  return `${chars.slice(0, 4)}-${chars.slice(4)}`
}

const enc = encodeURIComponent

export const pairRequestQuery = (userCode: string) =>
  queryOptions({
    queryKey: ["runners", "pair", userCode],
    queryFn: () => apiJson(`/api/runners/pair/${enc(userCode)}`, PairRequest),
    retry: false,
  })

export const approvePairing = (userCode: string, workspaceId: string) =>
  apiJson(`/api/runners/pair/${enc(userCode)}/approve`, Approved, { method: "POST", body: { workspaceId } })

export const denyPairing = (userCode: string) =>
  apiRequest(`/api/runners/pair/${enc(userCode)}/deny`, null, { method: "POST" })

/**
 * H8 runner list. Wave E (decision 0063): no 5 s poll any more. Presence, names and Just me / The team arrive over
 * `WorkspaceLiveRpcs.WatchRunners` (`use-runners-live.ts` merges them into this cache and refetches the owner's extra
 * fields shortly after); the slow refetch only catches what a dropped socket missed.
 */
export const runnersQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "runners"],
    queryFn: () => apiJson(`/api/workspaces/${enc(workspaceId)}/runners`, RunnerList),
    refetchInterval: 60_000,
    retry: false,
  })

const runnerPath = (workspaceId: string, runnerId: string) =>
  `/api/workspaces/${enc(workspaceId)}/runners/${enc(runnerId)}`

/** Console actions answer `{ delivered, runner }`: whether the runner got it live, and the updated row. */
const ConsoleResult = Schema.Struct({
  delivered: Schema.optionalKey(Schema.Boolean),
  runner: Schema.optionalKey(RunnerRow),
})
export type ConsoleResult = typeof ConsoleResult.Type

const consoleRequest = async (
  path: string,
  init: { method: string; body?: unknown },
): Promise<ConsoleResult> => {
  // The body is a courtesy (it saves a poll); an older server may send none or another shape.
  try {
    return (await apiRequest(path, ConsoleResult, init)) ?? {}
  } catch (error) {
    if (Schema.isSchemaError(error)) return {}
    throw error
  }
}

export const patchRunnerConfig = (workspaceId: string, runnerId: string, patch: ConfigPatch) =>
  consoleRequest(`${runnerPath(workspaceId, runnerId)}/config`, { method: "PATCH", body: patch })

export const refreshRunner = (workspaceId: string, runnerId: string) =>
  consoleRequest(`${runnerPath(workspaceId, runnerId)}/refresh`, { method: "POST" })

export const updateRunner = (workspaceId: string, runnerId: string) =>
  consoleRequest(`${runnerPath(workspaceId, runnerId)}/update`, { method: "POST" })

const consoleErrors: Record<string, string> = {
  forbidden: "Only the runner's owner can do that.",
  not_found: "This runner no longer exists.",
  offline: "The runner is offline. Try again when it's connected.",
  up_to_date: "The runner is already up to date.",
  no_release: "No runner release has been published yet.",
  no_build_for_platform: "The latest release has no build for this runner's platform.",
  unsupported_platform: "Updates aren't available for this runner's platform.",
  release_not_found: "That release doesn't exist.",
  unknown_profile: "That profile no longer exists on the runner. Refresh to see its current profiles.",
  runner_outdated: "Update the runner to use this setting (needs Minga 0.3.0).",
  // wave E (decision 0063): PATCH serves
  runner_not_owned: "Only the machine's owner can share it or make it just theirs again.",
  runner_too_old: "Update the runner to share it (needs Minga 0.8.0).",
  rekey_failed: "Couldn't give the machine a new key. Switch again.",
  invalid_body: "The server refused that value.",
}

/** "boom" → "boom.", so messages from the server read as sentences. */
export const asSentence = (text: string): string => {
  const t = text.trim()
  return t === "" || /[.!?…)]$/.test(t) ? t : `${t}.`
}

/** A sentence for a failed console action (config, refresh, update). */
export const consoleErrorMessage = (error: Error): string => asSentence(consoleErrorText(error))

const consoleErrorText = (error: Error): string => {
  if (!(error instanceof ApiError)) return error.message
  const code = typeof error.body?.error === "string" ? error.body.error : null
  const known = code ? consoleErrors[code] : undefined
  if (known) return known
  if (error.status === 403) return consoleErrors.forbidden ?? error.message
  if (error.status === 404) return "This server doesn't support that yet."
  return error.message
}

export const revokeRunner = (workspaceId: string, runnerId: string) =>
  apiRequest(`/api/workspaces/${enc(workspaceId)}/runners/${enc(runnerId)}`, null, { method: "DELETE" })

/**
 * Decision 0128: a local profile (a CLI login) Personal (only my work) or Team (anyone's), by the runner's owner.
 * Decision 0166 §3.1: or shared in some projects (`projectIds`).
 */
export const setRunnerProfileScope = (
  workspaceId: string,
  runnerId: string,
  profileId: string,
  scope: ProfileScope,
  projectIds: ReadonlyArray<string> = [],
) =>
  apiRequest(
    `/api/workspaces/${enc(workspaceId)}/runners/${enc(runnerId)}/profiles/${enc(profileId)}/scope`,
    null,
    { method: "PUT", body: scope === "projects" ? { scope, projectIds } : { scope } },
  )

/**
 * Decision 0128: a profile's owner and Personal/Team as this page shows it: the explicit one, else its runner owner's
 * login (Team on a machine shared with the team, else Personal).
 */
export const profileScopeOf = (
  row: Pick<RunnerRow, "ownerUserId" | "serves" | "profileAccess">,
  profileId: string,
): {
  readonly ownerId: string
  readonly scope: ProfileScope
  readonly projectIds: ReadonlyArray<string>
  readonly explicit: boolean
} => {
  const set = row.profileAccess?.[profileId]
  return set
    ? { ownerId: set.ownerId, scope: set.scope, projectIds: set.projectIds ?? [], explicit: true }
    : {
        ownerId: row.ownerUserId,
        scope: row.serves === "team" ? "team" : "personal",
        projectIds: [],
        explicit: false,
      }
}

/**
 * Plain-language text for a failed pairing call. The server answers with codes such as
 * 409 `{ error: "not_pending", status }`, 410 `{ status: "expired" }` and 403 `{ error: "forbidden" }`.
 */
export const pairingErrorMessage = (error: Error): string => {
  if (!(error instanceof ApiError)) return error.message
  switch (error.status) {
    case 403:
      return "You're not a member of that workspace, so you can't add runners to it."
    case 404:
      return "This pairing code is unknown. Start pairing again on the runner for a fresh code."
    case 409: {
      const status = error.body?.status
      const decided =
        status === "approved" || status === "consumed"
          ? "already approved"
          : status === "denied"
            ? "already denied"
            : "already approved or denied"
      return `This request was ${decided}, here or by another member. Nothing more to do.`
    }
    case 410:
      return "This request expired. Start pairing again on the runner for a fresh code."
    default:
      return error.message
  }
}

/** H16: rename a runner (its owner only). Answers the updated row. */
export const renameRunner = async (workspaceId: string, runnerId: string, name: string) =>
  apiJson(`/api/workspaces/${enc(workspaceId)}/runners/${enc(runnerId)}`, RunnerRow, {
    method: "PATCH",
    body: { name },
  })

/**
 * Wave E (decision 0063): switch a machine Just me (`owner`) / The team (`team`), its owner only. Every switch gives
 * the machine a new key (the hub re-keys it, then closes its sockets). Answers the updated row.
 */
export const setRunnerServes = async (workspaceId: string, runnerId: string, serves: "owner" | "team") =>
  apiJson(`/api/workspaces/${enc(workspaceId)}/runners/${enc(runnerId)}`, RunnerRow, {
    method: "PATCH",
    body: { serves },
  })

/** True when the runner reports the manifest feature (settings wave §5.2). Absent = an older runner. */
export const hasFeature = (
  runner: { readonly features?: ReadonlyArray<string> | undefined },
  feature: string,
) => (runner.features ?? []).includes(feature)

/** H18 `409 runner_outdated`: the runner must update before it takes this setting. */
export const isRunnerOutdated = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 409 && error.body?.error === "runner_outdated"
